import { BankCard, Card, UnifiedCard } from '../integrations/allkort/graphql/interface/Customer'

/**
 * Merge regular cards and bank cards into a unified array
 * 
 * @param regularCards - Array of regular Card objects from GET_CARDS_CUSTOMER_DATA_QUERY
 * @param bankCards - Array of BankCard objects from GET_BANKCARDS_CUSTOMER_DATA_QUERY
 * @returns Unified array of UnifiedCard objects
 */
export const mergeCardsData = (
	regularCards: Card[] = [],
	bankCards: BankCard[] = []
): UnifiedCard[] => {
	const merged: UnifiedCard[] = []

	// Add regular cards
	regularCards.forEach((card) => {
		merged.push({
			id: card.id,
			type: {
				name: card.type.name,
				shortName: card.type.shortName
			},
			number: card.number,
			maskedPan: card.maskedPan,  // Include if merged from display fields query
			name: card.reference2 || card.reference1 || card.name,  // Prefer custom reference, then fallback references
			isDisabled: card.limit?.disabled?.isDisabled || false,
			limit: {
				disabled: {
					at: card.limit?.disabled?.at || null,
					isDisabled: card.limit?.disabled?.isDisabled || false
				}
			},
			cardType: 'regular'
			// issuer intentionally omitted (undefined) for regular cards
		})
	})

	// Add bank cards
	bankCards.forEach((bankCard) => {
		merged.push({
			id: bankCard.id,
			type: {
				name: bankCard.type.name
			},
			name: bankCard.name,
			maskedPan: bankCard.maskedPan,
			issuer: bankCard.issuer,  // Bank cards have issuer (Visa, Mastercard, etc.)
			isDisabled: false,  // Bank cards are never disabled in the system
			limit: {
				disabled: {
					at: null,
					isDisabled: false
				}
			},
			cardType: 'bankcard'
		})
	})

	return merged
}

/**
 * Get the Swedish friendly name for a card type
 */
export const getFriendlyCardTypeName = (card: UnifiedCard, isCompany: boolean = false): string => {
	if (card.cardType === 'bankcard') {
		return 'bankkort'
	}
	
	if (card.type.shortName === 'P') {
		return 'smartkort'
	}
	
	if (card.type.shortName === 'K') {
		return isCompany ? 'företagskort' : 'tankkort'
	}
	
	return ''
}

/**
 * Filter cards by name or type name
 * 
 * @param cards - Array of unified cards
 * @param searchTerm - Term to search for
 * @param isCompany - Whether this is a company account (needed for correct P card naming)
 * @returns Filtered array
 */
export const searchCards = (cards: UnifiedCard[], searchTerm: string, isCompany: boolean = false): UnifiedCard[] => {
	const lowerSearchTerm = searchTerm.toLowerCase()
	const searchTermNoSpaces = searchTerm.replace(/\s/g, '')
	
	return cards.filter((card) => {
		// Search by name (case-insensitive)
		if (card.name?.toLowerCase().includes(lowerSearchTerm)) return true
		
		// Search by friendly Swedish card type name (smartkort, tankkort, företagskort, bankkort)
		const friendlyName = getFriendlyCardTypeName(card, isCompany)
		if (friendlyName && friendlyName.includes(lowerSearchTerm)) return true
		
		// For regular cards only: search by original type name (internal use only)
		if (card.cardType === 'regular' && card.type.name.toLowerCase().includes(lowerSearchTerm)) return true
		
		// Search by masked PAN - both with and without spaces (if available)
		if (card.maskedPan) {
			const panLowerNoSpaces = card.maskedPan.toLowerCase().replace(/\s/g, '')
			if (panLowerNoSpaces.includes(searchTermNoSpaces)) return true
			
			// Also check with original formatting in case user types it exactly
			if (card.maskedPan.toLowerCase().includes(lowerSearchTerm)) return true
		}
		
		return false
	})
}

/**
 * Advanced filter for cards with multiple filter options
 * 
 * @param cards - Array of unified cards
 * @param searchTerm - Search term (name, type, or card number)
 * @param typeFilters - Array of card type filters (e.g., ['K', 'P', 'B'])
 * @param includeDisabled - If true, show ONLY disabled cards matching other filters. If false, hide disabled cards by default.
 * @param isCompany - Whether this is a company account (for correct P card naming)
 * @returns Filtered array
 */
export const filterCards = (
	cards: UnifiedCard[],
	searchTerm: string = '',
	typeFilters: string[] = [],
	includeDisabled: boolean = false,
	isCompany: boolean = false
): UnifiedCard[] => {
	let filtered = [...cards]

	// Apply search filter
	if (searchTerm.trim()) {
		filtered = searchCards(filtered, searchTerm, isCompany)
	}

	// Apply type filters (only if filters are specified)
	if (typeFilters.length > 0) {
		filtered = filtered.filter((card) => {
			// Check for bank cards (cardType === 'bankcard' when user filters by 'B')
			if (typeFilters.includes('B') && card.cardType === 'bankcard') {
				return true
			}
			// Check for regular cards (type.shortName matches K or P)
			if (card.cardType === 'regular' && typeFilters.includes(card.type.shortName || '')) {
				return true
			}
			return false
		})
	}

	// Apply disabled filter
	if (includeDisabled) {
		// Show ONLY disabled cards (after type filtering if applicable)
		filtered = filtered.filter((card) => {
			return card.cardType === 'regular' && card.isDisabled === true
		})
	} else {
		// By default, hide disabled cards (hide disabled cards UNLESS includeDisabled is true)
		filtered = filtered.filter((card) => {
			return !card.isDisabled
		})
	}

		
	return filtered
}
